import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, CardActionArea, CardMedia, Grid2, LinearProgress, Link, useTheme } from '@mui/material';
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import AttachmentInput from '../attachment/attachment.input';
import AttachmentList from '../attachment/attachment.list';
import { useMenu } from '../common/dialog.util';
import ScheduleChip from '../common/schedule.chip';
import { getRangeLabel, lighter } from '../common/util';
import { DialogsContext } from '../contexts';
import { enrollmentApi } from "./enrollment.queries";
import ScoreAvatar from './score.avatar';
import { useNavigate } from 'react-router-dom';
import { useSnackbarStore } from "../app/store";

export default function EnrollmentCard(props) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const useDelete = enrollmentApi.useDelete();
	const useReschedule = enrollmentApi.useReschedule();
	const useSubscribe = enrollmentApi.useSubscribe();

	const [enrollment, setEnrollment] = useState(props.enrollment);
	useEffect(() => {
		setEnrollment(props.enrollment);
	}, [props.enrollment]);

	const theme = useTheme();
	const menu = useMenu();
	const { dialogs } = useContext(DialogsContext);

	if (!enrollment?.id || !enrollment?.student) {
		return;
	}

	const href = "/courses/" + enrollment.courseId + "/enrollments/" + enrollment.id;
	const linkActive = !window.location.pathname.startsWith("/courses/" + enrollment.courseId);

	const student = enrollment.student;

	let dateLabel = getRangeLabel(enrollment.startDate, enrollment.endDate, '\'YY', 'MMM');

	const progressSx = {
		flexGrow: 1,
		height: 8,
		'.MuiLinearProgress-bar1Buffer':{backgroundColor:lighter(student.colour, 0.2, theme)},
		'.MuiLinearProgress-bar2Buffer':{backgroundColor:lighter(student.colour, 0.6, theme)},
	};
	
	const onSelect = async (file) => {
		enrollment.attachments = enrollment.attachments ? enrollment.attachments : [];
		enrollment.attachments.push(file);
	};

	const onUpload = async (file) => {
		_.remove(enrollment.attachments, {fileName: file.fileName});
		enrollment.attachments.push(file);
		queryClient.invalidateQueries(['course', enrollment.courseId]);
	};
		
	const onDeleteAttachment = async(attachment) => {
		_.remove(enrollment.attachments, { id: attachment.id });
		queryClient.invalidateQueries(['course', enrollment.courseId]);
	};

	const onDelete = async (id) => {
		await useDelete.mutateAsync(id);
		window.location.reload();
	};

	const onReschedule = async (id) => {
		await useReschedule.mutateAsync(id);
		window.location.reload();
	};

	const onSubscribe = async(id) => {
		const result = await useSubscribe.mutateAsync(id);
		const url = window.location.origin + '/api/enrollments/calendar/' + result.token + '.ics';
		await navigator?.clipboard?.writeText(url);
		useSnackbarStore.getState().show('Calendar subscription URL copied to clipboard', "success");
	};

	const optionsMenu = (
		<Box>
			<IconButton className="hover-muted" onClick={(e) => menu.open(e)} sx={{ mr: -1 }}>
				<MoreVertIcon />
			</IconButton>
			<Menu open={menu.isOpen} onClose={() => menu.close()} anchorEl={menu.anchor}>
				<MenuItem onClick={() => { menu.close(); dialogs.open('enrollment', enrollment) }}>Edit</MenuItem>
				<MenuItem onClick={() => { menu.close(); dialogs.open('enrollmentReschedule', enrollment) }}>Reschedule...</MenuItem>
				<MenuItem onClick={() => { menu.close(); dialogs.open('enrollmentUnschedule', enrollment) }}>Unschedule...</MenuItem>
				{/* <MenuItem onClick={() => { menu.close(); dialogs.open('enrollmentUnenroll', enrollment) }}>Uneroll...</MenuItem> */}
				<MenuItem component="label" role={undefined}>
					Upload Attachment
					<AttachmentInput attachableType="enrollment" attachableId={enrollment.id} onSelect={(file) => { menu.close(); onSelect(file) }} onUpload={(file) => onUpload(file)} />
				</MenuItem>
				<Divider />
				<MenuItem onClick={() => { onSubscribe(enrollment.id); }}>Subscribe</MenuItem>
				<Divider />
				<MenuItem onClick={() => { menu.close(); onDelete(enrollment.id) }}>Delete</MenuItem>
			</Menu>
		</Box>
	);

	const cardMediaSx = { height: '100%', aspectRatio: '1 / 1' };
	const cardMedia = (
		<CardMedia sx={cardMediaSx} component="img" src={student?.profilePicture || '/missing-student.png'} />
	);

	let studentName = <Typography variant="h6">{student.name}</Typography>
	if (!linkActive) {
		studentName = <Link href={'/students/' + student.id}>{studentName}</Link>
	}

	const cardContent = (
		<CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
			<Stack spacing={1}>
				<Stack sx={{ flex: 1 }} direction="column" spacing={1}>
					{studentName}
					<Typography variant="subtitle2">{dateLabel}</Typography>
				</Stack>
				<Stack direction="column" alignItems="end">
					<ScoreAvatar score={enrollment.score} size={24} />
					<ScheduleChip schedule={enrollment.schedule} />
				</Stack>

				{!linkActive && optionsMenu}
			</Stack>

			<Box sx={{ flexGrow: 1 }}></Box>
			{enrollment.completeness > 0 && (
				<Box sx={{ ml: -4, mr: -2 }}>
					<LinearProgress title={enrollment.completeness?.toFixed(0) + '% Complete'} variant="buffer" value={enrollment.completeness} valueBuffer={100} sx={progressSx} color="info" />
				</Box>
			)}
		</CardContent>
	);

	let cardDetail = (
		<Grid2 container sx={{ width: '100%' }}>
			<Grid2 size={3}>
				{cardMedia}
			</Grid2>
			<Grid2 size={9} sx={{ display: 'flex' }}>
				{cardContent}
			</Grid2>
		</Grid2>
	);

	if (linkActive) {
		cardDetail = (
			<CardActionArea href={href} sx={{ display: 'flex', textDecoration: 'none', height: '100%' }}>
				{cardDetail}
			</CardActionArea>
		);
	}

	const colour = student?.colour || theme.palette.info.main;
	const cardSx = { width: '100%', background: lighter(colour, 0.8, theme) };
	return (
		<Card sx={cardSx} className="hover-toggle">
			{cardDetail}
			{props.showAttachments == true && enrollment.attachments?.length > 0 &&
				<Box sx={{ p: 1 }}>
					<Divider sx={{ mb: 1 }} />
					<AttachmentList attachments={enrollment.attachments} onDelete={(attachment) => onDeleteAttachment(attachment)} />
				</Box>
			}
		</Card>
	)
}